/**
 * 後台 — 販售品項列表
 *
 * 顯示所有 SKU(含 inactive)+ 篩選 category + 上下架 toggle + 點 row 進編輯。
 * 第一次進來若 Firestore 內沒任何 SKU,跳「seed 既有 11 SKU」按鈕(一次性 migration)。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListProducts,
  adminListProductCategories,
  adminSeedProducts,
  adminToggleProductActive,
  type AdminProduct,
  type ProductCategoryMeta,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


export default function ProductList() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [rows, setRows] = useState<AdminProduct[]>([]);
  const [cats, setCats] = useState<ProductCategoryMeta[]>([]);
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function refresh() {
    setLoading(true);
    try {
      const [products, categories] = await Promise.all([
        adminListProducts(),
        adminListProductCategories(),
      ]);
      setRows(products);
      setCats(categories);
    } catch {
      showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive(p: AdminProduct) {
    const newActive = !p.active;
    try {
      const r = await adminToggleProductActive(p.id, newActive);
      if (r.status === "ok") {
        showToast(newActive ? "已上架" : "已下架", "success");
        await refresh();
      } else {
        showToast(`操作未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  async function runSeed() {
    setSeeding(true);
    try {
      const r = await adminSeedProducts(false);
      showToast(`匯入完成:新增 ${r.created} 筆 / 略過 ${r.skipped} 筆`, "success");
      await refresh();
    } catch {
      showToast("匯入失敗", "error");
    } finally {
      setSeeding(false);
    }
  }

  const filtered = filterCat ? rows.filter((r) => r.category === filterCat) : rows;
  const catLabel = (k: string) => cats.find((c) => c.key === k)?.title || k;

  // 空 collection 引導 seed
  if (!loading && rows.length === 0) {
    return (
      <div className="min-h-screen bg-cream">
        <AdminHeader active="products" />
        <div className="p-6">
          <div className="card text-center py-10 space-y-4">
            <div className="text-lg font-bold">尚未匯入商品資料</div>
            <div className="text-sm text-muted max-w-md mx-auto">
              首次啟用販售品項管理,請先匯入既有 11 項 SKU(從程式內 hardcode 資料)。
              匯入後仍可在這裡編輯、上下架、新增。
            </div>
            <button
              onClick={runSeed}
              disabled={seeding}
              className="btn-primary"
            >
              {seeding ? "匯入中…" : "匯入既有 11 項 SKU"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="products" />

      <div className="p-6">
        <div className="card mb-5 flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">分類</label>
            <select
              className="input min-w-32"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
            >
              <option value="">全部</option>
              {cats.map((c) => (
                <option key={c.key} value={c.key}>{c.title}</option>
              ))}
            </select>
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => navigate("/admin/categories")}
              className="btn-secondary"
            >
              分類管理
            </button>
            <button
              onClick={() => navigate("/admin/products/new")}
              className="btn-primary"
            >
              ＋ 新增商品
            </button>
          </div>
        </div>

        <div className="card">
          {loading ? (
            <div className="py-10 text-center text-muted">載入中…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted">此分類無商品</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted text-xs border-b border-line">
                  <th className="py-3">分類</th>
                  <th className="py-3">商品名</th>
                  <th className="py-3">規格</th>
                  <th className="py-3 text-right">售價</th>
                  <th className="py-3 text-right">瓶數</th>
                  <th className="py-3 text-center">狀態</th>
                  <th className="py-3 text-right">排序</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-line/50 hover:bg-cream/50 cursor-pointer transition"
                    onClick={() => navigate(`/admin/products/${p.id}`)}
                  >
                    <td className="py-3 text-muted text-xs">{catLabel(p.category)}</td>
                    <td className="py-3 font-medium">{p.name}</td>
                    <td className="py-3 text-muted">{p.spec}</td>
                    <td className="py-3 text-right">NT$ {(p.price || 0).toLocaleString()}</td>
                    <td className="py-3 text-right text-muted text-xs">
                      {p.bottle_count > 0 ? `${p.bottle_count} 瓶` : "—"}
                    </td>
                    <td className="py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActive(p);
                        }}
                        className={`text-xs px-2 py-1 rounded ${
                          p.active
                            ? "bg-primary text-white"
                            : "bg-muted/30 text-muted"
                        }`}
                      >
                        {p.active ? "上架中" : "已下架"}
                      </button>
                      {p.storefront_hidden && (
                        <div className="text-[10px] text-muted mt-1">前台隱藏</div>
                      )}
                    </td>
                    <td className="py-3 text-right text-muted text-xs">{p.sort_order}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-xs text-muted mt-3 text-right">共 {filtered.length} 筆</div>
        </div>
      </div>
    </div>
  );
}
